import { BookOpen, ShieldCheck } from 'lucide-react'
import { PageHeader } from '../shell/PageHeader'
import { Card, CardContent } from '@/components/ui/Card'
import { AISparkle } from '@/components/ui/AISparkle'

const SOURCES = [
  { name: 'NEJM', full: 'New England Journal of Medicine', desc: 'Landmark randomized trials and clinical reviews.' },
  { name: 'JAMA', full: 'Journal of the American Medical Assoc.', desc: 'Clinical research and practice guidelines.' },
  { name: 'BMJ', full: 'The BMJ', desc: 'Evidence-based practice and rapid recommendations.' },
  { name: 'The Lancet', full: 'The Lancet', desc: 'Global clinical and public-health evidence.' },
  { name: 'Cochrane', full: 'Cochrane Library', desc: 'Gold-standard systematic reviews & meta-analyses.' },
  { name: 'USPSTF', full: 'US Preventive Services Task Force', desc: 'Screening & prevention recommendations.' },
  { name: 'ACC/AHA', full: 'Am. College of Cardiology / Am. Heart Assoc.', desc: 'Blood pressure, lipids, ASCVD risk guidelines.' },
  { name: 'ADA', full: 'ADA Standards of Care', desc: 'Diabetes management; glycemic & CGM targets.' },
  { name: 'ESC', full: 'European Society of Cardiology', desc: 'Cardiovascular guidelines.' },
  { name: 'UpToDate', full: 'UpToDate', desc: 'Point-of-care clinical decision support.' },
]

export function KnowledgePage({ onMenu, showMenu }: { onMenu: () => void; showMenu: boolean }) {
  return (
    <div className="flex h-full flex-1 min-w-0 flex-col bg-background">
      <PageHeader title="Knowledge base" subtitle="What SARA reasons from" onMenu={onMenu} showMenu={showMenu} />
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-4xl px-4 md:px-6 py-6 space-y-6">
          <Card>
            <CardContent className="flex items-start gap-3 pt-6">
              <AISparkle size="md" className="mt-0.5" />
              <div>
                <h2 className="text-sm font-semibold text-foreground">Grounded in evidence, not the open web</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  SARA's coaching draws on peer-reviewed medical literature and major clinical
                  guidelines — applied to <span className="text-foreground font-medium">your</span> record. She
                  reasons from sources like the ones below, not unverified search results, and tells you
                  when something needs a clinician's judgment.
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-3 grid-cols-1">
            {SOURCES.map((s) => (
              <div key={s.name} className="lift flex gap-3 rounded-xl border border-border bg-card p-4">
                <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-accent">
                  <BookOpen className="size-4 text-accent-foreground" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-semibold text-foreground">{s.name}</span>
                    <span className="truncate text-[11px] text-muted-foreground">{s.full}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-start gap-3 rounded-xl border border-border-subtle bg-success-light p-4">
            <ShieldCheck className="size-5 shrink-0 text-success-text mt-0.5" />
            <p className="text-sm text-foreground">
              SARA is a coach and guide — not a doctor. She does not diagnose or prescribe. When evidence
              points to something needing clinical evaluation, she routes you to the right level of care.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
